import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../backend/firebaseConfig";
import { fetchLogoUrl } from "./FetchLogo";

const _liveStore = new Map();

function norm(v) {
  return (v || "").toString().trim().toLowerCase();
}

function emptyResult(key) {
  return {
    key,
    adviserId: null,
    adviserDoc: null,
    logoUrl: "",
    brandName: "",
    personalDetails: null,
    applicationDetails: null,
    previousAddresses: null,
    nextOfKinDetails: null,
    professionalQualifications: null,
    licenseCertificates: {},
    rates: {},
    loading: true,
  };
}

function emit(entry) {
  for (const cb of entry.callbacks) {
    try {
      cb(entry.value);
    } catch {}
  }
}

function stopSubListeners(entry) {
  entry.unsubs.slice(1).forEach((u) => {
    try {
      u && u();
    } catch {}
  });

  entry.unsubs = entry.unsubs.slice(0, 1);
  entry.currentAdviserId = null;
}

function stopEntry(key) {
  const entry = _liveStore.get(key);
  if (!entry) return;

  entry.unsubs.forEach((u) => {
    try {
      u && u();
    } catch {}
  });

  _liveStore.delete(key);
}

export function subscribeAdviserFullDetailsByEmailOrId(emailOrId, onValue) {
  const key = norm(emailOrId);
  if (!key || typeof onValue !== "function") return () => {};

  let entry = _liveStore.get(key);

  if (!entry) {
    entry = {
      callbacks: new Set(),
      value: emptyResult(key),
      unsubs: [],
      started: false,
      currentAdviserId: null,
    };

    _liveStore.set(key, entry);
  }

  entry.callbacks.add(onValue);
  onValue(entry.value);

  if (!entry.started) {
    entry.started = true;

    const advisersRef = collection(
      db,
      "Authorized Members",
      "Advisers",
      "Data",
    );

    const unsubAdvisers = onSnapshot(
      advisersRef,
      (snap) => {
        let match = null;

        for (const d of snap.docs) {
          const data = d.data() || {};

          if (norm(d.id) === key || norm(data.email) === key) {
            match = d;
            break;
          }
        }

        if (!match) {
          stopSubListeners(entry);
          entry.value = { ...emptyResult(key), loading: false };
          emit(entry);
          return;
        }

        const adviserId = match.id;
        const base = match.data() || {};

        if (entry.currentAdviserId !== adviserId) {
          stopSubListeners(entry);
          entry.currentAdviserId = adviserId;
        }

        entry.value = {
          ...entry.value,
          adviserId,
          adviserDoc: base,
          loading: false,
        };

        emit(entry);

        const brandName =
          base?.brandName || base?.tradingName || base?.tradingStyle || "";

        fetchLogoUrl(brandName).then((logoUrl) => {
          if (entry.currentAdviserId !== adviserId) return;

          entry.value = {
            ...entry.value,
            logoUrl,
            brandName,
          };

          emit(entry);
        });

        if (entry.unsubs.length > 1) return;

        const personalRef = doc(
          db,
          "Authorized Members",
          "Advisers",
          "Data",
          adviserId,
          "Personal Details",
          "Data",
        );

        const appRef = doc(
          db,
          "Authorized Members",
          "Advisers",
          "Data",
          adviserId,
          "Application Details",
          "Data",
        );

        const prevAddrRef = doc(
          db,
          "Authorized Members",
          "Advisers",
          "Data",
          adviserId,
          "Application Details",
          "Previous Addresses",
        );

        const nokRef = doc(
          db,
          "Authorized Members",
          "Advisers",
          "Data",
          adviserId,
          "Application Details",
          "Next of Kin Details",
        );

        const profQualsRef = doc(
          db,
          "Authorized Members",
          "Advisers",
          "Data",
          adviserId,
          "Application Details",
          "Professional Qualifications",
        );

        const licenseCertificatesRef = doc(
          db,
          "Authorized Members",
          "Advisers",
          "Data",
          adviserId,
          "License Certificates",
          "Data",
        );

        const ratesRef = doc(
          db,
          "Authorized Members",
          "Advisers",
          "Data",
          adviserId,
          "Rates",
          "Data",
        );

        entry.unsubs.push(
          onSnapshot(personalRef, (d) => {
            entry.value = {
              ...entry.value,
              personalDetails: d.exists() ? d.data() : null,
            };
            emit(entry);
          }),

          onSnapshot(appRef, (d) => {
            entry.value = {
              ...entry.value,
              applicationDetails: d.exists() ? d.data() : null,
            };
            emit(entry);
          }),

          onSnapshot(prevAddrRef, (d) => {
            entry.value = {
              ...entry.value,
              previousAddresses: d.exists() ? d.data() : null,
            };
            emit(entry);
          }),

          onSnapshot(nokRef, (d) => {
            entry.value = {
              ...entry.value,
              nextOfKinDetails: d.exists() ? d.data() : null,
            };
            emit(entry);
          }),

          onSnapshot(profQualsRef, (d) => {
            entry.value = {
              ...entry.value,
              professionalQualifications: d.exists() ? d.data() : null,
            };
            emit(entry);
          }),

          onSnapshot(licenseCertificatesRef, (d) => {
            entry.value = {
              ...entry.value,
              licenseCertificates: d.exists() ? d.data() : {},
            };
            emit(entry);
          }),

          onSnapshot(ratesRef, (d) => {
            const savedRates = d.exists() ? d.data() : {};
            entry.value = {
              ...entry.value,
              rates: savedRates,
            };
            emit(entry);
          }),
        );
      },
      () => {
        stopSubListeners(entry);
        entry.value = { ...emptyResult(key), loading: false };
        emit(entry);
      },
    );

    entry.unsubs.push(unsubAdvisers);
  }

  return () => {
    const e = _liveStore.get(key);
    if (!e) return;

    e.callbacks.delete(onValue);

    if (e.callbacks.size === 0) {
      stopEntry(key);
    }
  };
}

export function subscribeAdviserLicenseCertificateDetailsById(
  adviserId,
  onValue,
) {
  const id = String(adviserId || "").trim();
  if (!id || typeof onValue !== "function") return () => {};

  let value = {
    ...emptyResult(id),
    adviserId: id,
  };
  const loaded = {
    adviserDoc: false,
    personalDetails: false,
    licenseCertificates: false,
  };

  const emitValue = () => {
    onValue({
      ...value,
      loading: !Object.values(loaded).every(Boolean),
    });
  };

  const handleSnapshot = (key, fallback) => (snapshot) => {
    loaded[key] = true;
    value = {
      ...value,
      [key]: snapshot.exists() ? snapshot.data() : fallback,
    };
    emitValue();
  };

  const handleError = (key, fallback) => () => {
    loaded[key] = true;
    value = { ...value, [key]: fallback };
    emitValue();
  };

  const adviserRef = doc(db, "Authorized Members", "Advisers", "Data", id);
  const personalRef = doc(
    db,
    "Authorized Members",
    "Advisers",
    "Data",
    id,
    "Personal Details",
    "Data",
  );
  const licenseCertificatesRef = doc(
    db,
    "Authorized Members",
    "Advisers",
    "Data",
    id,
    "License Certificates",
    "Data",
  );

  const unsubscribers = [
    onSnapshot(
      adviserRef,
      handleSnapshot("adviserDoc", null),
      handleError("adviserDoc", null),
    ),
    onSnapshot(
      personalRef,
      handleSnapshot("personalDetails", null),
      handleError("personalDetails", null),
    ),
    onSnapshot(
      licenseCertificatesRef,
      handleSnapshot("licenseCertificates", {}),
      handleError("licenseCertificates", {}),
    ),
  ];

  return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
}

export function stopAllAdviserLiveListeners() {
  for (const key of _liveStore.keys()) {
    stopEntry(key);
  }
}
